import { Component, inject, OnInit, signal } from '@angular/core';

import { Router } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { NotificationService } from '@angular-monorepo/data-access';
import { MenuItem } from 'primeng/api';
import {
  CardActionType,
  CardConfig,
  CardListComponent,
  DataSourceConfig,
  StatusFilterValue,
} from '../../components/workflow/card/card-list.component';
import { BoilerplateWorkflowItem } from '../../interfaces/api-response.interfaces';
import { MonitoringService, WorkflowFilters } from '../../services/monitoring.service';

@Component({
  selector: 'lib-monitoring',
  imports: [CardListComponent],
  templateUrl: './monitoring.component.html',
  styleUrl: './monitoring.component.css',
  standalone: true,
})
export class MonitoringComponent implements OnInit {
  public breadcrumbs = signal<MenuItem[]>([]);
  public dataSource$!: Observable<unknown>;
  public isLoading = signal(true);
  public currentSearch = signal<string>('');
  public currentStatusFilter = signal<StatusFilterValue>('all');

  public dataSourceConfig: DataSourceConfig = {
    dataPath: 'data',
    totalRecordsPath: 'total',
    totalPagesPath: undefined,
    pageIndexPath: 'page',
    successPath: undefined,
  };

  public cardConfig: CardConfig = {
    titleKey: 'name',
    clickable: true,
    themeCount: 8,
    fields: [
      {
        label: 'ID',
        key: 'id',
        type: 'text',
      },
      {
        label: 'Descripción',
        key: 'description',
        type: 'text',
      },
      {
        label: 'Estado',
        key: 'enabled',
        nestedKey: 'periodic_task.enabled',
        type: 'status',
        statusConfig: {
          activeValue: true,
          activeLabel: 'Activo',
          inactiveLabel: 'Inactivo',
          activeClass:
            'text-xs text-emerald-700 bg-emerald-100 py-1 px-2 rounded border border-emerald-200 dark:text-emerald-300',
          inactiveClass:
            'text-xs text-amber-700 bg-amber-100 py-1 px-2 rounded border border-amber-200 dark:text-amber-300',
        },
      },
      {
        label: 'Frecuencia',
        key: 'schedule',
        nestedKey: 'periodic_task.schedule',
        type: 'pipe',
        pipe: 'cronFriendly',
      },
      {
        label: 'Ejecuciones',
        key: 'total_run_count',
        nestedKey: 'periodic_task.total_run_count',
        type: 'text',
      },
    ],
  };
  public currentPageKey = signal(0);
  private monitoringService = inject(MonitoringService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  ngOnInit(): void {
    this.setupBreadcrumbs();
    this.setupDataSource();
  }

  public onWorkflowClick(workflow: BoilerplateWorkflowItem): void {
    console.log('Workflow clicked:', workflow);
    this.router
      .navigate(['monitoreo/workflow', workflow.id], {
        state: { name: workflow.name },
      })
      .then((r) => r);
  }

  public onPageChange(event: { limit: number; offset: number; page?: number }): void {
    this.isLoading.set(true);

    const page = event.page ?? 1;
    const filters = this.buildWorkflowFilters(this.currentSearch());

    this.dataSource$ = this.monitoringService
      .getWorkflows(event.limit, page, filters)
      .pipe(finalize(() => this.isLoading.set(false)));

    this.currentPageKey.update((v) => v + 1);
  }

  public onSearchChange(event: {
    search: string;
    limit: number;
    offset: number;
    page?: number;
  }): void {
    this.isLoading.set(true);
    this.currentSearch.set(event.search);

    const page = event.page ?? 1;
    const filters = this.buildWorkflowFilters(event.search);

    this.dataSource$ = this.monitoringService
      .getWorkflows(event.limit, page, filters)
      .pipe(finalize(() => this.isLoading.set(false)));

    this.currentPageKey.update((v) => v + 1);
  }

  public onStatusFilterChange(statusValue: StatusFilterValue): void {
    this.currentStatusFilter.set(statusValue);
    this.reloadFirstPage();
  }

  public hasActiveFilters(): boolean {
    return this.currentSearch().trim().length > 0 || this.currentStatusFilter() !== 'all';
  }

  public clearServerFilters(): void {
    this.currentSearch.set('');
    this.currentStatusFilter.set('all');
    this.reloadFirstPage();
  }

  public createNewWorkflow(): void {
    console.log('Create new workflow');
  }

  public onCardAction(event: { action: CardActionType; item: BoilerplateWorkflowItem }): void {
    if (event.action !== 'run-now') {
      return;
    }

    const workflowId = event.item?.id;
    if (!workflowId) {
      this.notificationService.showError('No se pudo obtener el ID del workflow');
      return;
    }

    this.isLoading.set(true);

    this.monitoringService.syncronizeWorkflowById(workflowId).subscribe({
      next: (response) => {
        this.isLoading.set(false);

        if (response.status === 'success') {
          this.notificationService.showSuccess(
            response.message || 'Workflow ejecutado exitosamente',
            'Ejecución Exitosa',
          );

          setTimeout(() => {
            this.reloadFirstPage();
          }, 1000);
          return;
        }

        this.notificationService.showWarning(
          response.message || 'La ejecución no se completó correctamente',
          'Ejecución Parcial',
        );
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al ejecutar workflow:', error);

        let errorMessage = 'Error al ejecutar el workflow';

        if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.notificationService.showError(errorMessage, 'Error de Ejecución');
      },
    });
  }

  private setupBreadcrumbs(): void {
    this.breadcrumbs.set([{ label: 'Workflows' }, { label: 'Workflows 2' }]);
  }

  private setupDataSource(): void {
    this.isLoading.set(true);
    const filters = this.buildWorkflowFilters(this.currentSearch());

    this.dataSource$ = this.monitoringService
      .getWorkflows(9, 1, filters)
      .pipe(finalize(() => this.isLoading.set(false)));
  }

  private reloadFirstPage(): void {
    this.isLoading.set(true);
    const filters = this.buildWorkflowFilters(this.currentSearch());

    this.dataSource$ = this.monitoringService
      .getWorkflows(9, 1, filters)
      .pipe(finalize(() => this.isLoading.set(false)));

    this.currentPageKey.update((v) => v + 1);
  }

  private buildWorkflowFilters(search: string): WorkflowFilters {
    const filters: WorkflowFilters = {};
    const trimmedSearch = search.trim();

    if (trimmedSearch) {
      filters.q = trimmedSearch;
    }

    if (this.currentStatusFilter() === 'true') {
      filters.is_enabled = true;
    } else if (this.currentStatusFilter() === 'false') {
      filters.is_enabled = false;
    }

    return filters;
  }
}
